/**
 * CANONICAL ANALYSIS STATE SERIALIZER
 * ====================================
 * 
 * INVARIANT: At any moment after analysis or recalculation, there must exist 
 * exactly ONE canonical final analysis state used by:
 * - Results UI
 * - Share snapshot
 * - PDF generation
 * - Email generation
 * 
 * NO output may compute from a different source.
 * 
 * This module provides:
 * 1. buildCanonicalState() - Produces the single source of truth
 * 2. serializeForShare() - Serializes canonical state for persistence
 * 3. computeDebugChecksum() - Generates checksum for verification
 * 4. logCanonicalChecksum() - Logs checksum at key points
 * 5. getCanonicalDecision() - Maps deal_classification to display label (SINGLE SOURCE OF TRUTH)
 */

/**
 * P0 FIX: CANONICAL DECISION MAPPING
 * ==================================
 * This is THE SINGLE FUNCTION for mapping deal_classification.decision to display labels.
 * ALL surfaces (Results, PDF, Share, Email) MUST use this function.
 * 
 * NEVER derive decision from deal_score or deal_rating.
 * ALWAYS use deal_classification.decision as the source of truth.
 * 
 * @param {Object} state - The canonical state or analysis object
 * @param {string} lang - Language code ('en' or 'es')
 * @returns {Object} { label, decision, color }
 */
export const getCanonicalDecision = (state, lang = 'en') => {
  // For rental strategy, use rental_classification instead of deal_classification
  const strategy = state?.strategy || '';
  
  if (strategy === 'rental') {
    const rc = state?.rental_classification || {};
    const rentalDecision = rc.decision || '';
    
    if (rentalDecision === 'BUY' || rentalDecision === 'FLEX_BUY') {
      return {
        decision: 'GOOD_DEAL',
        label: lang === 'en' ? 'STRONG RENTAL' : 'ALQUILER FUERTE',
        color: 'green',
        rating: 'strong'
      };
    } else if (rentalDecision === 'SPECULATIVE') {
      return {
        decision: 'MARGINAL',
        label: lang === 'en' ? 'MODERATE RENTAL' : 'ALQUILER MODERADO',
        color: 'amber',
        rating: 'moderate'
      };
    } else {
      return {
        decision: 'NO_PASS',
        label: lang === 'en' ? 'WEAK RENTAL' : 'ALQUILER DÉBIL',
        color: 'red',
        rating: 'not_recommended'
      };
    }
  }
  
  // Extract deal_classification.decision as the SINGLE SOURCE OF TRUTH
  const dc = state?.deal_classification || {};
  const decision = dc.decision || ''; // GOOD_DEAL, MARGINAL, NO_PASS
  
  // Map to display labels
  if (decision === 'GOOD_DEAL') {
    return {
      decision: 'GOOD_DEAL',
      label: lang === 'en' ? 'BUY' : 'COMPRAR',
      color: 'green',
      rating: 'strong'
    };
  } else if (decision === 'MARGINAL') {
    return {
      decision: 'MARGINAL',
      label: lang === 'en' ? 'NEGOTIATE' : 'NEGOCIAR',
      color: 'amber',
      rating: 'moderate'
    };
  } else if (decision === 'NO_PASS') {
    return {
      decision: 'NO_PASS',
      // AVOID, never "PASS" (which reads as "passed/approved" for a rejected deal).
      label: lang === 'en' ? 'AVOID' : 'EVITAR',
      color: 'red',
      rating: 'not_recommended'
    };
  }
  
  // FALLBACK: If deal_classification is missing, check deal_rating (backward compatibility)
  // This should only happen with very old analysis data
  const rating = state?.deal_rating || 'moderate';
  if (rating === 'strong') {
    return {
      decision: 'GOOD_DEAL',
      label: lang === 'en' ? 'BUY' : 'COMPRAR',
      color: 'green',
      rating: 'strong'
    };
  } else if (rating === 'moderate') {
    return {
      decision: 'MARGINAL',
      label: lang === 'en' ? 'NEGOTIATE' : 'NEGOCIAR',
      color: 'amber',
      rating: 'moderate'
    };
  }
  return {
    decision: 'NO_PASS',
    // AVOID, never "PASS" (backward-compat fallback path).
    label: lang === 'en' ? 'AVOID' : 'EVITAR',
    color: 'red',
    rating: 'not_recommended'
  };
};

/**
 * Compute a debug checksum object containing critical fields
 * Used to verify state consistency across all surfaces
 * 
 * @param {Object} state - The canonical state object
 * @returns {Object} Checksum object with key fields
 */
export const computeDebugChecksum = (state) => {
  if (!state) {
    return {
      valid: false,
      financing_mode: null,
      arv: null,
      net_profit: null,
      loan_costs: null,
      decision: null,
      comp_count: null,
      timestamp: new Date().toISOString()
    };
  }

  const flip = state.flip_analysis || {};
  const dc = state.deal_classification || {};
  const arvDetails = state.arv_details || {};
  
  // Determine financing mode from multiple possible sources
  const financingMode = 
    state.financing_mode || 
    state.assumptions?.purchase_financing || 
    (state.financing_mode_obj?.purchase) ||
    'unknown';
  
  // Count comps
  const compCount = arvDetails.final_comps?.length || 
    state.comparable_count || 
    state.sales_comps?.length || 
    0;

  return {
    valid: true,
    financing_mode: financingMode,
    arv: flip.arv || state.arv || 0,
    net_profit: flip.net_profit || 0,
    loan_costs: flip.loan_costs || 0,
    decision: dc.decision || state.deal_rating || 'unknown',
    comp_count: compCount,
    timestamp: new Date().toISOString()
  };
};

/**
 * Log the canonical checksum at a specific flow point
 * 
 * @param {string} flowPoint - Where in the flow this is being logged
 * @param {Object} state - The canonical state object
 */
export const logCanonicalChecksum = (flowPoint, state) => {
  const checksum = computeDebugChecksum(state);
  
  console.log(`[CANONICAL CHECKSUM @ ${flowPoint}]`, {
    financing_mode: checksum.financing_mode,
    arv: `$${checksum.arv?.toLocaleString() || 0}`,
    net_profit: `$${checksum.net_profit?.toLocaleString() || 0}`,
    loan_costs: `$${checksum.loan_costs?.toLocaleString() || 0}`,
    decision: checksum.decision,
    comp_count: checksum.comp_count,
    timestamp: checksum.timestamp
  });
  
  return checksum;
};

/**
 * Build the canonical analysis state from raw analysis and current UI state
 * 
 * This is THE SINGLE FUNCTION that produces the authoritative state.
 * All surfaces MUST consume from this output.
 * 
 * @param {Object} params - Input parameters
 * @param {Object} params.analysis - Raw analysis from backend
 * @param {Object} params.propertyOverrides - User property overrides
 * @param {number} params.editedPurchasePrice - User-edited purchase price
 * @param {number} params.currentARV - Current ARV (may be from user comps)
 * @param {string} params.currentARVConfidence - Current ARV confidence level
 * @param {number} params.currentARVConfidenceScore - Current ARV confidence score
 * @param {Object} params.assumptions - Current financial assumptions
 * @param {string} params.arvModeLabel - Label for ARV mode (api_only, user_only, etc.)
 * @param {Object} params.classificationCriteria - User's deal criteria
 * @returns {Object} The canonical analysis state
 */
export const buildCanonicalState = ({
  analysis,
  propertyOverrides = {},
  editedPurchasePrice = null,
  currentARV = null,
  currentARVConfidence = null,
  currentARVConfidenceScore = null,
  assumptions = {},
  arvModeLabel = 'api_only',
  classificationCriteria = {}
}) => {
  if (!analysis) return null;

  // Build unified property snapshot with overrides
  const propertySnapshot = {
    ...(analysis.property_snapshot || {}),
    beds: propertyOverrides.beds ?? analysis.property_snapshot?.beds ?? analysis.beds,
    baths: propertyOverrides.baths ?? analysis.property_snapshot?.baths ?? analysis.baths,
    sqft: propertyOverrides.sqft ?? analysis.property_snapshot?.sqft ?? analysis.sqft,
    year_built: propertyOverrides.year_built ?? analysis.property_snapshot?.year_built ?? analysis.year_built,
    property_type: propertyOverrides.property_type ?? analysis.property_snapshot?.property_type ?? 'Single Family',
    lot_size: propertyOverrides.lot_size ?? analysis.property_snapshot?.lot_size,
  };

  // Determine financing mode
  const financingMode = assumptions.purchaseFinancing || 'cash';

  // Build the canonical state
  const canonicalState = {
    // ==========================================
    // METADATA
    // ==========================================
    analysis_id: analysis.analysis_id,
    strategy: analysis.strategy || 'flip',
    type: 'canonical_state',
    created_at: new Date().toISOString(),

    // ==========================================
    // PROPERTY IDENTIFICATION
    // ==========================================
    address: analysis.address || propertySnapshot.address,
    city: analysis.city || propertySnapshot.city,
    state: analysis.state || propertySnapshot.state,
    zip_code: analysis.zip_code || propertySnapshot.zip_code,
    property_type: propertySnapshot.property_type,
    beds: propertySnapshot.beds,
    baths: propertySnapshot.baths,
    sqft: propertySnapshot.sqft,
    year_built: propertySnapshot.year_built,
    property_snapshot: propertySnapshot,

    // ==========================================
    // VALUATION
    // ==========================================
    purchase_price: editedPurchasePrice ?? analysis.purchase_price,
    arv: currentARV || analysis.flip_analysis?.arv || analysis.arv,
    arv_confidence: currentARVConfidence || analysis.arv_confidence,
    arv_confidence_score: currentARVConfidenceScore || analysis.arv_confidence_score,
    arv_details: analysis.arv_details,
    arv_mode: arvModeLabel,
    // ONE VOICE: the ARV mode the document is on and its verdict travel with the state.
    // The PDF printed "Modo ARV: Combinado" on a User Only analysis because this
    // whitelist carried only the page-local label (null after a reload) and the PDF
    // read active_arv_mode — absent here — and fell back to blended.
    active_arv_mode: analysis.active_arv_mode,
    arv_mode_label: analysis.arv_mode_label || arvModeLabel || null,
    arv_by_mode: analysis.arv_by_mode,
    verdict_reliable: analysis.verdict_reliable,
    system_comps_used: analysis.system_comps_used,
    user_comps_used: analysis.user_comps_used,
    rehab_estimate: analysis.rehab_estimate,
    rehab_breakdown: analysis.rehab_breakdown,

    // ==========================================
    // DEAL CLASSIFICATION (FROM BACKEND)
    // No independent calculation allowed
    // ==========================================
    deal_score: analysis.deal_score,
    deal_rating: analysis.deal_rating,
    deal_classification: analysis.deal_classification,
    deal_decision: analysis.deal_decision,
    deal_explanation: analysis.deal_explanation,
    score_breakdown: analysis.score_breakdown,

    // ==========================================
    // ANALYSIS RESULTS (FROM BACKEND)
    // ==========================================
    flip_analysis: analysis.flip_analysis,
    rental_analysis: analysis.rental_analysis,
    rental_classification: analysis.rental_classification,
    rent_estimate: analysis.rent_estimate,

    // ==========================================
    // FINANCING STATE
    // ==========================================
    financing_mode: financingMode,
    financing_mode_obj: {
      purchase: assumptions.purchaseFinancing || 'cash',
      rehab: assumptions.rehabFinancing || 'cash',
    },

    // ==========================================
    // USER CRITERIA
    // ==========================================
    user_criteria: {
      min_profit: classificationCriteria.min_profit || 20000,
      min_margin: classificationCriteria.min_margin || 10,
    },

    // ==========================================
    // ASSUMPTIONS
    // ==========================================
    // This bundle is a WHITELIST: only what is listed here survives the Edit-Criteria
    // round-trip. Anything the user can edit on the Results page must appear below, or
    // it silently reverts to the backend's original value on navigation. Keep it in sync
    // with getDefaultAssumptions() in Results.js — that function is the read side, and a
    // key written here with no matching reader round-trips to nothing.
    assumptions: {
      ...analysis.assumptions,
      purchase_financing: assumptions.purchaseFinancing,
      rehab_financing: assumptions.rehabFinancing,
      holding_period: assumptions.holdingPeriod,
      arv_multiplier: assumptions.maoRule ? assumptions.maoRule / 100 : 0.70,
      // BUG 3: the user's EDITED holding costs were never carried here — only
      // `...analysis.assumptions` (the backend's original values). Because the
      // Edit-Criteria round-trip saves/restores this bundle, an edited property-tax
      // figure silently reverted while other fields appeared to survive.
      property_taxes: assumptions.propertyTaxes,
      insurance: assumptions.insurance,
      utilities: assumptions.utilities,
      other_holding_costs: assumptions.otherHoldingCosts,
      // ---- RENTAL operating costs (annual $ unless noted) ----
      rental_annual_taxes: assumptions.rentalAnnualTaxes,
      rental_annual_insurance: assumptions.rentalAnnualInsurance,
      rental_utilities: assumptions.rentalUtilities,
      // BUG 5: the opex PERCENTAGES and HOA were missing entirely, so a user who set
      // vacancy to 3% and management to 0% got 5%/10% back on every navigation — the
      // cash flow they had just tuned reverted with them.
      rental_vacancy_pct: assumptions.rentalVacancyPct,
      rental_maintenance_pct: assumptions.rentalMaintenancePct,
      rental_management_pct: assumptions.rentalManagementPct,
      rental_hoa: assumptions.rentalHoa,                 // MONTHLY
      rental_other_opex: assumptions.rentalOtherOpex,
      // ---- RENTAL income ----
      rental_monthly_rent: assumptions.rentalMonthlyRent,
      // ---- RENTAL financing terms ----
      rental_financing_type: assumptions.rentalFinancingType,
      rental_down_payment_pct: assumptions.rentalDownPaymentPct,
      rental_interest_rate: assumptions.rentalInterestRate,
      rental_loan_term_years: assumptions.rentalLoanTermYears,
      rental_loan_points: assumptions.rentalLoanPoints,
      rental_property_condition: assumptions.rentalPropertyCondition,
      rental_purchase_type: assumptions.rentalPurchaseType,
      // The user's own value must survive navigation — and stay marked as theirs.
      user_arv_override: assumptions.userArvOverride ?? null,
      // Refinance inputs, so a refi survives navigation as a refi
      appraised_value: assumptions.rentalAppraisedValue,
      existing_payoff: assumptions.rentalExistingPayoff,
      refi_ltv: assumptions.rentalRefiLtv,
      // ---- RENTAL closing costs (BUG 4: entered up front, editable on the page) ----
      rental_closing_costs_pct: assumptions.rentalClosingCostsPct,
      rental_origination_points: assumptions.rentalOriginationPoints,
      rental_lender_fees: assumptions.rentalLenderFees,
      rental_title_attorney_fees: assumptions.rentalTitleAttorneyFees,
      rental_escrow_reserves: assumptions.rentalEscrowReserves,
      rental_other_closing_costs: assumptions.rentalOtherClosingCosts,
      rental_seller_agent_commission: assumptions.rentalSellerAgentCommission,
    },

    // ==========================================
    // COMPARABLES
    // ==========================================
    sales_comps: analysis.sales_comps,
    rental_comps: analysis.rental_comps,
    comparable_count: analysis.comparable_count,
    // P3/UNIFY: carry the EXACT comps the ARV used (+ excluded) so the PDF renders the
    // SAME set as the web. Without this, the PDF fell back to the raw sales_comps pool
    // (header showed comparable_count, table showed 6 raw comps — divergence).
    comps_used: analysis.comps_used,
    comps_excluded: analysis.comps_excluded,

    // ==========================================
    // RISK/OPPORTUNITY
    // ==========================================
    risk_factors: analysis.risk_factors,
    opportunities: analysis.opportunities,

    // ==========================================
    // FLAGS
    // ==========================================
    has_user_overrides: Object.keys(propertyOverrides).length > 0,
  };

  return canonicalState;
};

/**
 * Serialize canonical state for share snapshot persistence
 * This produces the EXACT object that will be stored and retrieved
 * 
 * @param {Object} canonicalState - The canonical state from buildCanonicalState
 * @returns {Object} Serialized state for API submission
 */
export const serializeForShare = (canonicalState) => {
  if (!canonicalState) return null;

  // Log checksum at share serialization
  logCanonicalChecksum('SHARE_SERIALIZE', canonicalState);

  return {
    // The API expects the state nested under 'analysis'
    analysis: {
      // All fields from canonical state
      address: canonicalState.address,
      city: canonicalState.city,
      state: canonicalState.state,
      zip_code: canonicalState.zip_code,
      property_type: canonicalState.property_type,
      beds: canonicalState.beds,
      baths: canonicalState.baths,
      sqft: canonicalState.sqft,
      year_built: canonicalState.year_built,
      
      purchase_price: canonicalState.purchase_price,
      arv: canonicalState.arv,
      arv_confidence: canonicalState.arv_confidence,
      arv_confidence_score: canonicalState.arv_confidence_score,
      rehab_estimate: canonicalState.rehab_estimate,
      
      deal_score: canonicalState.deal_score,
      deal_rating: canonicalState.deal_rating,
      deal_classification: canonicalState.deal_classification,
      deal_decision: canonicalState.deal_decision,
      deal_explanation: canonicalState.deal_explanation,
      strategy: canonicalState.strategy,
      
      flip_analysis: canonicalState.flip_analysis,
      rental_analysis: canonicalState.rental_analysis,
      
      financing_mode: canonicalState.financing_mode,
      arv_mode: canonicalState.arv_mode,
      active_arv_mode: canonicalState.active_arv_mode,
      arv_mode_label: canonicalState.arv_mode_label,
      verdict_reliable: canonicalState.verdict_reliable,
      
      comparable_count: canonicalState.comparable_count,
      comps_used: canonicalState.comps_used,
      comps_excluded: canonicalState.comps_excluded,
      property_snapshot: canonicalState.property_snapshot,
      user_criteria: canonicalState.user_criteria,
      arv_details: canonicalState.arv_details,
    }
  };
};

/**
 * Deserialize shared analysis from API response
 * Produces a canonical state object for rendering
 * 
 * @param {Object} apiResponse - Raw API response from getSharedAnalysis
 * @returns {Object} Canonical state for rendering
 */
export const deserializeFromShare = (apiResponse) => {
  if (!apiResponse) return null;

  // Log checksum at share load
  logCanonicalChecksum('SHARE_LOAD', apiResponse);

  // The API response IS the canonical state - no transformation needed
  // Just ensure consistent structure
  return {
    type: apiResponse.type || 'share_snapshot',
    analysis_id: apiResponse.id || apiResponse.analysis_id,
    
    // Property
    address: apiResponse.address,
    city: apiResponse.city,
    state: apiResponse.state,
    zip_code: apiResponse.zip_code,
    property_type: apiResponse.property_type,
    beds: apiResponse.beds,
    baths: apiResponse.baths,
    sqft: apiResponse.sqft,
    year_built: apiResponse.year_built,
    property_snapshot: apiResponse.property_snapshot,
    
    // Valuation
    purchase_price: apiResponse.purchase_price,
    arv: apiResponse.arv,
    arv_confidence: apiResponse.arv_confidence,
    arv_mode: apiResponse.arv_mode,
    rehab_estimate: apiResponse.rehab_estimate,
    
    // Deal assessment
    deal_score: apiResponse.deal_score,
    deal_rating: apiResponse.deal_rating,
    deal_classification: apiResponse.deal_classification,
    deal_decision: apiResponse.deal_decision,
    deal_explanation: apiResponse.deal_explanation,
    user_criteria: apiResponse.user_criteria,
    
    // Financing
    financing_mode: apiResponse.financing_mode,
    
    // Analysis results
    flip_analysis: apiResponse.flip_analysis,
    rental_analysis: apiResponse.rental_analysis,
    strategy: apiResponse.strategy,
    
    // Comps
    comparable_count: apiResponse.comparable_count,
    comps_used: apiResponse.comps_used,
    comps_excluded: apiResponse.comps_excluded,
    arv_details: apiResponse.arv_details,
  };
};

/**
 * Serialize canonical state for PDF generation
 * 
 * @param {Object} canonicalState - The canonical state
 * @param {string} userId - User ID for the PDF
 * @returns {Object} Payload for PDF prepare endpoint
 */
export const serializeForPDF = (canonicalState, userId = 'guest') => {
  if (!canonicalState) return null;

  logCanonicalChecksum('PDF_SERIALIZE', canonicalState);

  return {
    analysis: {
      address: canonicalState.address,
      city: canonicalState.city,
      state: canonicalState.state,
      zip_code: canonicalState.zip_code,
      purchase_price: canonicalState.purchase_price,
      arv: canonicalState.arv,
      strategy: canonicalState.strategy,
      flip_analysis: canonicalState.flip_analysis,
      rental_analysis: canonicalState.rental_analysis,
      deal_classification: canonicalState.deal_classification,
      deal_score: canonicalState.deal_score,
      deal_rating: canonicalState.deal_rating,
      arv_details: canonicalState.arv_details,
      property_snapshot: canonicalState.property_snapshot,
      financing_mode: canonicalState.financing_mode,
      user_criteria: canonicalState.user_criteria,
    },
    user_id: userId
  };
};

/**
 * Serialize canonical state for email report
 * 
 * @param {Object} canonicalState - The canonical state
 * @param {string} recipientEmail - Email recipient
 * @param {string} senderName - Sender's name
 * @returns {Object} Payload for email endpoint
 */
export const serializeForEmail = (canonicalState, recipientEmail, senderName = 'CHEKSTE User') => {
  if (!canonicalState) return null;

  logCanonicalChecksum('EMAIL_SERIALIZE', canonicalState);

  return {
    to_email: recipientEmail,
    sender_name: senderName,
    analysis: {
      address: canonicalState.address,
      city: canonicalState.city,
      state: canonicalState.state,
      zip_code: canonicalState.zip_code,
      purchase_price: canonicalState.purchase_price,
      arv: canonicalState.arv,
      strategy: canonicalState.strategy,
      flip_analysis: canonicalState.flip_analysis,
      rental_analysis: canonicalState.rental_analysis,
      deal_classification: canonicalState.deal_classification,
      deal_score: canonicalState.deal_score,
      deal_rating: canonicalState.deal_rating,
      financing_mode: canonicalState.financing_mode,
      user_criteria: canonicalState.user_criteria,
      property_snapshot: canonicalState.property_snapshot,
    }
  };
};

export default {
  buildCanonicalState,
  serializeForShare,
  deserializeFromShare,
  serializeForPDF,
  serializeForEmail,
  computeDebugChecksum,
  logCanonicalChecksum,
};
